import { useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { TrendingUp } from 'lucide-react';
import { api } from '@/lib/api';
import { trackGaragePulseImpression } from '@/lib/analytics';

export function GaragePulseCard() {
  const { data, isLoading } = useQuery({
    queryKey: ['garages', 'trending'],
    queryFn: () => api.garages.trending({ limit: 5, days: 7 }),
    staleTime: 60 * 60 * 1000,
  });

  const impressed = useRef(false);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (impressed.current || !data?.items?.length) return;
    const el = cardRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !impressed.current) {
          impressed.current = true;
          trackGaragePulseImpression(data.items.length, data.items[0].name);
        }
      },
      { threshold: 0.5 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [data]);

  if (isLoading || !data?.items?.length) return null;

  const topGarage = data.items[0];
  // T-136: this widget said '5 garages active this week' / '5 members' while
  // the Spotlight card for the SAME garage on the SAME page said '1 members'.
  // The widget was the liar, twice over:
  //   activeCount was `items.length` — the PAGE SIZE of /garages/trending
  //   (limit defaults to 5). It counted rows returned, not garages active, so
  //   it read '5 garages active' even if every one had zero activity.
  //   totalMembers summed memberCount across the page, presenting a sum over
  //   an arbitrary top-N slice as though it were a community total.
  // Count only garages with real activity in the window, and drop the
  // members line rather than print a number whose scope we cannot state.
  const activeCount = data.items.filter(
    (g) => (g.newPosts ?? 0) > 0 || (g.newMembers ?? 0) > 0
  ).length;
  // Nothing actually happened this week: show no card rather than '0 garages
  // active this week'. Real content stays (T-127: this widget is public and
  // must not be gated away when it HAS something true to say).
  if (activeCount === 0) return null;

  return (
    <div ref={cardRef} className="mt-6 rounded-xl border border-border-primary bg-bg-secondary p-3">
      <Link to="/garages" className="flex items-center gap-2 text-xs font-semibold text-emerald-400 hover:text-emerald-300 transition-colors">
        <TrendingUp className="h-4 w-4" />
        Garage Pulse
      </Link>
      <div className="mt-2 space-y-1.5 text-xs text-text-secondary">
        <div className="flex items-center justify-between">
          <span>{activeCount} garages active this week</span>
        </div>
        <Link
          to={`/garage/${topGarage.slug}`}
          className="block truncate font-medium text-primary-400 hover:text-primary-300 transition-colors"
        >
          Top: {topGarage.name}
          {topGarage.newPosts > 0 && (
            <span className="ml-1 text-emerald-500">+{topGarage.newPosts}</span>
          )}
        </Link>
      </div>
    </div>
  );
}
